import React, { useState } from 'react';
import { Property } from '../../types';
import { Card } from '../cards/Card';
import { Button } from '../buttons/Button';
import { Badge } from '../common/Badge';
import { X, Copy, Check, Mail, MapPin } from 'lucide-react';

interface PropertyEmailComposerProps {
    properties: Property[];
    agentName: string;
    agentPhone?: string;
    onClose: () => void;
}

export const PropertyEmailComposer: React.FC<PropertyEmailComposerProps> = ({
    properties,
    agentName,
    agentPhone,
    onClose
}) => {
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
    const [copied, setCopied] = useState(false);

    const toggleProperty = (propertyId: string) => {
        setSelectedPropertyIds(prev =>
            prev.includes(propertyId)
                ? prev.filter(id => id !== propertyId)
                : [...prev, propertyId]
        );
    };

    const calculateAvailability = (property: Property): { available: number; total: number; status: string } => {
        // Check if property has rooms or beds
        if (property.rooms && property.rooms.length > 0) {
            const availableRooms = property.rooms.filter(r => r.available !== false).length;
            const totalRooms = property.rooms.length;

            if (availableRooms === 0) return { available: 0, total: totalRooms, status: 'Fully Booked' };
            if (availableRooms < totalRooms) return { available: availableRooms, total: totalRooms, status: `Limited: ${availableRooms} space${availableRooms > 1 ? 's' : ''} available` };
            return { available: availableRooms, total: totalRooms, status: `${availableRooms} spaces available` };
        }

        if (property.beds && property.beds.length > 0) {
            const availableBeds = property.beds.filter(b => b.available !== false).length;
            const totalBeds = property.beds.length;

            if (availableBeds === 0) return { available: 0, total: totalBeds, status: 'Fully Booked' };
            if (availableBeds < totalBeds) return { available: availableBeds, total: totalBeds, status: `Limited: ${availableBeds} bed${availableBeds > 1 ? 's' : ''} available` };
            return { available: availableBeds, total: totalBeds, status: `${availableBeds} beds available` };
        }

        // Fallback for whole-unit properties
        if (property.status === 'AVAILABLE') {
            return { available: 1, total: 1, status: 'Available' };
        }
        return { available: 0, total: 1, status: 'Rented' };
    };

    const generateEmailContent = () => {
        const selectedProperties = properties.filter(p => selectedPropertyIds.includes(p.id));

        let emailText = `Dear Student,\n\n`;
        emailText += `I hope this message finds you well. I have the following properties available that might interest you:\n\n`;
        emailText += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

        selectedProperties.forEach((property, index) => {
            const availability = calculateAvailability(property);
            const propertyUrl = `https://rooms.indigobeans.co.za/room/${property.slug || property.id}`;

            emailText += `${index + 1}. ${property.title}\n`;
            emailText += `📍 ${property.locationName || property.location}\n`;
            emailText += `💰 R${property.price}/month\n`;
            emailText += `🏠 ${property.type} | ${property.bedrooms} Bed | ${property.bathrooms} Bath\n`;
            emailText += `✅ Status: ${availability.status}\n`;
            emailText += `🔗 View Details: ${propertyUrl}\n`;
            emailText += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
        });

        emailText += `If you're interested in any of these properties or would like to schedule a viewing, please don't hesitate to reach out.\n\n`;
        emailText += `Best regards,\n${agentName}\n`;
        if (agentPhone) {
            emailText += `📱 ${agentPhone}\n`;
        }
        emailText += `IndigoBeans Rooms\n`;

        return emailText;
    };

    const handleCopyToClipboard = () => {
        const emailContent = generateEmailContent();
        navigator.clipboard.writeText(emailContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleEmailClient = () => {
        const emailContent = generateEmailContent();
        const subject = `Available Student Accommodation - ${selectedPropertyIds.length} ${selectedPropertyIds.length === 1 ? 'Property' : 'Properties'}`;
        const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailContent)}`;
        window.open(mailtoLink, '_blank');
    };

    const selectedProperties = properties.filter(p => selectedPropertyIds.includes(p.id));

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 animate-scaleIn">
                {/* Header */}
                <div className="bg-[#2f5c96] px-6 py-4 flex justify-between items-center text-white sticky top-0 z-10 rounded-t-xl">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5" /> Email Properties to Students
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/10 rounded-full p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto space-y-6">
                    {/* Property Selection */}
                    <div>
                        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            Select Properties ({selectedPropertyIds.length} selected)
                        </h4>
                        <div className="space-y-3">
                            {properties.map(property => {
                                const isSelected = selectedPropertyIds.includes(property.id);
                                const availability = calculateAvailability(property);
                                const isAvailable = availability.available > 0;

                                return (
                                    <Card
                                        key={property.id}
                                        className={`p-4 cursor-pointer transition-all ${isSelected
                                                ? 'border-2 border-[#2f5c96] bg-blue-50/30'
                                                : 'hover:border-gray-300 hover:shadow-md'
                                            } ${!isAvailable ? 'opacity-60' : ''}`}
                                        onClick={() => isAvailable && toggleProperty(property.id)}
                                    >
                                        <div className="flex items-start gap-4">
                                            {/* Checkbox */}
                                            <div className="pt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => isAvailable && toggleProperty(property.id)}
                                                    disabled={!isAvailable}
                                                    className="w-5 h-5 text-[#2f5c96] rounded focus:ring-2 focus:ring-[#2f5c96] cursor-pointer"
                                                />
                                            </div>

                                            {/* Property Image */}
                                            <div className="w-24 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                                <img
                                                    src={property.image}
                                                    alt={property.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Property Details */}
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h5 className="font-bold text-gray-900">{property.title}</h5>
                                                    <span className="font-bold text-[#2f5c96] ml-2">R{property.price}/mo</span>
                                                </div>
                                                <div className="flex items-center text-sm text-gray-600 mb-2">
                                                    <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                                                    <span className="truncate">{property.locationName || property.location}</span>
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Badge color={isAvailable ? 'green' : 'gray'}>
                                                        {availability.status}
                                                    </Badge>
                                                    <span className="text-xs text-gray-500">
                                                        {property.bedrooms} Bed • {property.bathrooms} Bath • {property.type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>

                    {/* Email Preview */}
                    {selectedProperties.length > 0 && (
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="font-bold text-gray-900 mb-4">Email Preview</h4>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                                    {generateEmailContent()}
                                </pre>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 mt-4">
                                <Button
                                    onClick={handleCopyToClipboard}
                                    className="flex-1 bg-[#2f5c96] hover:bg-[#254a7a]"
                                >
                                    {copied ? (
                                        <>
                                            <Check className="w-4 h-4 mr-2" /> Copied!
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-4 h-4 mr-2" /> Copy to Clipboard
                                        </>
                                    )}
                                </Button>
                                <Button
                                    onClick={handleEmailClient}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    <Mail className="w-4 h-4 mr-2" /> Open Email Client
                                </Button>
                            </div>

                            <p className="text-xs text-gray-500 mt-3 text-center">
                                Copy the text above to paste into any email client, or use the "Open Email Client" button to create a draft automatically.
                            </p>
                        </div>
                    )}

                    {selectedProperties.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p>Select at least one property to generate an email</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
